import { DEFAULT_AUTHOR_KEY } from './siteConfig.js';

const CATEGORY_COLORS = {
  'Language Model': ['#14b8a6', '#0f172a'],
  'Vector Database': ['#64748b', '#0f172a'],
  'Orchestration': ['#06b6d4', '#0f172a'],
  'Code Assistant': ['#22c55e', '#0f172a'],
  'Agents': ['#8b5cf6', '#111827'],
  'Image Generation': ['#f43f5e', '#111827'],
  'Audio/Voice': ['#ec4899', '#111827'],
  'Embeddings': ['#3b82f6', '#0f172a'],
  'Deployment': ['#f59e0b', '#111827'],
  'Observability': ['#a855f7', '#111827'],
  'Guardrails': ['#ef4444', '#111827'],
  'Search': ['#3b82f6', '#0f172a'],
  'Video Generation': ['#e879f9', '#111827'],
  'Data Processing': ['#a78bfa', '#111827'],
  'Evaluation': ['#84cc16', '#111827'],
  default: ['#14b8a6', '#0f172a'],
};

function compactText(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/[“”]/g, '"')
    .replace(/[’]/g, "'")
    .trim();
}

function sentenceCase(value) {
  const text = compactText(value);
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function toSentence(text) {
  const compact = compactText(text);
  if (!compact) return '';
  return /[.!?]$/.test(compact) ? compact : `${compact}.`;
}

function wordCount(text) {
  return compactText(text).split(/\s+/).filter(Boolean).length;
}

function listToSentence(values = [], maxItems = 3) {
  const items = values
    .filter(Boolean)
    .map((value) => compactText(value))
    .filter(Boolean)
    .slice(0, maxItems);

  if (!items.length) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function cleanMarketingCopy(value) {
  let text = compactText(value);
  if (!text) return '';

  const replacements = [
    [/\bunlock\b/gi, 'use'],
    [/\bgroundbreaking\b/gi, 'advanced'],
    [/\brevolutionary\b/gi, 'AI-enabled'],
    [/\bcutting-edge\b/gi, 'current'],
    [/\bunparalleled\b/gi, 'broad'],
    [/\bworld-class\b/gi, 'production-focused'],
    [/\bbest-in-class\b/gi, 'specialized'],
    [/\bstunning\b/gi, 'high-quality'],
    [/\bexceptional\b/gi, 'strong'],
    [/\bempower(?:s|ing|ed)?\b/gi, 'support'],
    [/\btransform(?:s|ing|ed)?\b/gi, 'change'],
    [/\bdiscover\b/gi, 'review'],
    [/\bseamlessly\b/gi, 'across'],
    [/\beffortless(?:ly)?\b/gi, 'with less setup'],
    [/\bperfect for\b/gi, 'often used by'],
    [/\bideal for\b/gi, 'commonly evaluated by'],
    [/\bnext generation\b/gi, 'current'],
    [/\bhigh-performance\b/gi, 'production-oriented'],
    [/\bon top of\b/gi, 'built on'],
  ];

  replacements.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });

  text = text
    .replace(/\b(start|get) [^.?!]*today\b/gi, '')
    .replace(/\bexperience the future of [^.?!]*[.?!]?/gi, '')
    .replace(/\bexplore the possibilities[^.?!]*[.?!]?/gi, '')
    .replace(/\bboost your productivity[^.?!]*[.?!]?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return sentenceCase(text);
}

function buildNeutralToolDescription(tool) {
  const name = compactText(tool.name) || 'This tool';
  const provider = compactText(tool.provider);
  const category = compactText(tool.category) || 'AI tool';
  const bestFor = compactText(tool.best_for).replace(/^best for[:\s-]*/i, '');
  const tagline = cleanMarketingCopy(tool.tagline);
  const useCases = listToSentence(tool.use_cases, 3);
  const keyFeatures = listToSentence(tool.key_features, 3);
  const originalDescription = cleanMarketingCopy(tool.description);

  const lead = provider
    ? `${name} is a ${category.toLowerCase()} tool from ${provider}.`
    : `${name} is a ${category.toLowerCase()} tool.`;

  if (bestFor) {
    return `${lead} It is commonly evaluated for ${bestFor.replace(/\.$/, '')}.`;
  }

  if (useCases) {
    return `${lead} Common use cases include ${useCases}.`;
  }

  if (keyFeatures) {
    return `${lead} It typically includes ${keyFeatures}.`;
  }

  if (tagline) {
    return `${lead} ${toSentence(tagline)}`;
  }

  if (originalDescription) {
    return `${lead} ${toSentence(originalDescription)}`;
  }

  return lead;
}

function buildGoodFit(tool) {
  const bestFor = compactText(tool.best_for).replace(/^best for[:\s-]*/i, '');
  if (bestFor) return toSentence(bestFor);

  if (tool.use_cases?.length) {
    return `You need support for ${listToSentence(tool.use_cases, 3)}.`;
  }

  if (tool.key_features?.length) {
    return `You need ${listToSentence(tool.key_features, 3)}.`;
  }

  return `You are evaluating ${compactText(tool.category || 'AI tools').toLowerCase()} options for a specific workflow.`;
}

function buildNotGoodFit(tool) {
  const limitation = compactText(tool.limitations?.[0]?.limitation)
    .replace(/\bnot best for\b/gi, 'Less suitable for')
    .replace(/\bbest for\b/gi, 'Less suitable for');
  if (limitation) return toSentence(limitation);

  const con = compactText(tool.cons?.[0])
    .replace(/\bnot best for\b/gi, 'Less suitable for')
    .replace(/\bbest for\b/gi, 'Less suitable for');
  if (con) return toSentence(con);

  const pricingModel = compactText(tool.pricing?.model);
  if (pricingModel === 'enterprise') {
    return 'You need self-serve pricing or low-friction evaluation before procurement.';
  }

  return 'You need requirements this tool does not document clearly enough for a confident fit decision.';
}

function normalizeDate(value) {
  const text = compactText(value);
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().split('T')[0];
}

function formatHumanDate(value) {
  const normalized = normalizeDate(value);
  if (!normalized) return '';
  const [year, month, day] = normalized.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function stripMarkdown(value) {
  return compactText(
    String(value || '')
      .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[*_`>#]/g, ' ')
      .replace(/\|/g, ' ')
  );
}

function sanitizeBlogText(value) {
  const replacements = [
    [/\bExceptional\b/g, 'Strong'],
    [/\bexceptional\b/g, 'strong'],
    [/\bRobust\b/g, 'Reliable'],
    [/\brobust\b/g, 'reliable'],
    [/\bseamless\b/gi, 'consistent'],
    [/\bleverage\b/gi, 'use'],
    [/\bempower(?:s|ing|ed)?\b/gi, 'support'],
    [/\btransform(?:s|ing|ed)?\b/gi, 'change'],
    [/\bgame-changing\b/gi, 'high-impact'],
    [/\brevolutionary\b/gi, 'current'],
    [/\bcutting-edge\b/gi, 'current'],
    [/\bbest practices\b/gi, 'common practices'],
    [/\bbest suited\b/gi, 'commonly used'],
    [/\bbest fit\b/gi, 'good fit'],
    [/\bnot recommended\b/gi, 'usually not a fit'],
    [/\brecommended\b/gi, 'commonly used'],
    [/\bleading\b/gi, 'established'],
    [/\bsuperior\b/gi, 'stronger in some cases'],
    [/\btop(?!-k\b)\b/gi, 'high-priority'],
    [/\bbest\b/gi, 'preferred'],
  ];

  let text = String(value || '');
  replacements.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });

  return text
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

function humanJoin(values = []) {
  const items = values.map((value) => compactText(value)).filter(Boolean);
  if (!items.length) return 'this topic';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function parseSections(content) {
  const matches = [...String(content || '').matchAll(/^(#{2,3})\s+(.+)$/gm)];
  if (matches.length === 0) {
    return { intro: compactText(content), sections: [] };
  }

  const intro = content.slice(0, matches[0].index).trim();
  const sections = matches.map((match, index) => {
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : content.length;
    return {
      level: match[1].length,
      heading: compactText(match[2]),
      body: content.slice(start, end).trim(),
    };
  });

  return { intro, sections };
}

function buildTopicLabel(post) {
  const toolsText = humanJoin(post.tools || []);
  if ((post.tools || []).length > 0) return toolsText;
  if (post.template === 'task-guide') {
    return compactText(post.title).replace(/^AI Tools for\s*/i, '').replace(/:.*$/, '');
  }
  return compactText(post.title).replace(/^What is\s+/i, '').replace(/:.*$/, '').replace(/\?$/, '');
}

function buildQuickAnswerHeading(post) {
  if (post.template === 'head-to-head' && (post.tools || []).length > 1) {
    return `What are the key differences between ${humanJoin(post.tools)}?`;
  }
  if (post.template === 'tool-profile') {
    return `What is ${buildTopicLabel(post)}?`;
  }
  if (post.template === 'task-guide' || post.template === 'workflow-pattern') {
    return `How should teams approach ${buildTopicLabel(post)}?`;
  }
  if (post.template === 'category-overview') {
    return `What does the ${buildTopicLabel(post)} landscape look like?`;
  }
  return "What's the quick answer?";
}

function toPossessive(name) {
  const text = compactText(name);
  return text.endsWith('s') ? `${text}'` : `${text}'s`;
}

function toQuestionHeading(heading, level, post) {
  const text = compactText(heading).replace(/^[#\s]+/, '').replace(/[.:]+$/, '');
  const lower = text.toLowerCase();
  const tools = post.tools || [];
  const topic = buildTopicLabel(post);

  if (!text) return level === 2 ? 'What should you know?' : 'What should you validate?';
  if (text.endsWith('?')) return text;
  if (lower === 'faq') return `What are common questions about ${topic}?`;
  if (lower === 'about touchstone') return 'What is Touchstone and how do we stay neutral?';
  if (lower === 'at a glance') return tools.length > 1 ? 'What does the at-a-glance comparison show?' : `What does ${topic} look like at a glance?`;
  if (lower === 'how it works') return `How does ${topic} work?`;
  if (lower === 'evidence basis') return 'What is the evidence basis?';
  if (lower === 'capability comparison') return 'How do the capabilities compare?';
  if (lower === 'good fit if') return tools.length > 1 ? `When is ${tools[0]} a good fit?` : `When is ${topic} a good fit?`;
  if (lower === 'not a good fit if') return `When is ${topic} not a good fit?`;
  if (lower === 'cost comparison' || lower === 'pricing' || lower === 'pricing comparison') return 'How does pricing compare?';
  if (lower === 'stack compatibility') return 'How does this fit into existing stacks?';
  if (lower === 'when to use both') return 'When does it make sense to use both tools together?';
  if (lower === 'getting started') return 'What should you know before getting started?';
  if (lower === 'implementation') return 'How is this implemented in practice?';
  if (lower === 'cost and roi') return 'What affects cost and ROI?';
  if (lower === 'security and compliance') return 'What security and compliance issues matter?';
  if (lower === 'troubleshooting') return 'What problems come up in practice?';
  if (/^choose .+ if$/i.test(text)) {
    return `When is ${text.replace(/^choose\s+/i, '').replace(/\s+if$/i, '')} a good fit?`;
  }
  if (/(.+) limitations$/i.test(text)) {
    const [, subject] = text.match(/(.+) limitations/i);
    return `What are ${toPossessive(subject)} limitations?`;
  }

  const generic = text.replace(/\s+/g, ' ').trim();
  return `${level === 2 ? 'What should you know about' : 'What matters about'} ${generic.replace(/\?$/, '')}?`;
}

function buildSectionLead(question, post) {
  const lower = question.toLowerCase();
  const topic = buildTopicLabel(post);

  if (lower.includes('touchstone')) {
    return 'Touchstone is a neutral research platform that documents how AI tools are used in practice without rankings, paid placements, or affiliate incentives.';
  }
  if (lower.includes('quick answer') || lower.includes('key differences')) {
    return post.directAnswer;
  }
  if (lower.includes('at-a-glance') || lower.includes('look like at a glance')) {
    return 'The table below summarizes the main evaluation criteria, operating model, and fit constraints in one place.';
  }
  if (lower.includes('evidence basis')) {
    return 'This analysis draws on public documentation, pricing pages, Touchstone research notes, and practitioner reports where available.';
  }
  if (lower.includes('capabilities compare')) {
    return 'The most important differences usually show up in capabilities, operational tradeoffs, and integration depth rather than marketing claims.';
  }
  if (lower.includes('pricing')) {
    return 'Pricing should be evaluated alongside limits, included features, deployment overhead, and the cost of supporting the workflow over time.';
  }
  if (lower.includes('existing stacks')) {
    return 'Stack fit depends on runtime constraints, deployment model, observability needs, and how much infrastructure your team wants to own.';
  }
  if (lower.includes('good fit')) {
    return `A good fit depends on the workflow, constraints, and priorities your team is optimizing for when evaluating ${topic}.`;
  }
  if (lower.includes('not a good fit') || lower.includes('limitations')) {
    return 'These limits matter when deployment model, cost, capability depth, or governance requirements conflict with your implementation needs.';
  }
  if (lower.includes('common questions')) {
    return `These are the recurring questions teams ask when evaluating ${topic} for a real workflow.`;
  }
  if (lower.includes('related touchstone articles')) {
    return 'The links below provide adjacent Touchstone coverage so you can compare fit, surrounding infrastructure, and implementation tradeoffs.';
  }
  if (lower.includes('security') || lower.includes('compliance')) {
    return 'Security and compliance review should happen before adoption, especially when tools handle sensitive data, credentials, or downstream actions.';
  }
  if (lower.includes('cost') || lower.includes('roi')) {
    return 'Cost depends on pricing model, operational overhead, and how much manual work the workflow actually replaces.';
  }
  if (lower.includes('troubleshooting') || lower.includes('problems')) {
    return 'Operational issues usually appear in setup quality, data quality, permissions, testing discipline, and monitoring coverage.';
  }
  if (lower.includes('how')) {
    return `This section explains the practical workflow behind ${topic} and what teams usually need to validate first.`;
  }

  return `This section explains the main practical consideration behind ${topic}.`;
}

function ensureBoldLead(body, question, post) {
  const trimmed = String(body || '').trim();
  if (!trimmed) {
    return `**${buildSectionLead(question, post)}**`;
  }

  if (trimmed.startsWith('**')) return trimmed;

  const lead = `**${buildSectionLead(question, post)}**`;
  return `${lead}\n\n${trimmed}`.trim();
}

function buildQuickAnswerSection(post, intro) {
  const heading = buildQuickAnswerHeading(post);
  const lead = compactText(post.directAnswer);
  const introText = sanitizeBlogText(intro || '');
  const bodyParts = [];

  if (lead) {
    bodyParts.push(`**${lead}**`);
  }

  if (introText) {
    const plainLead = stripMarkdown(lead).toLowerCase();
    const plainIntro = stripMarkdown(introText).toLowerCase();
    if (!plainLead || !plainIntro.includes(plainLead.slice(0, 60))) {
      bodyParts.push(introText);
    }
  }

  return {
    level: 2,
    heading,
    body: bodyParts.join('\n\n').trim(),
  };
}

function extractExistingFaqs(content) {
  const body = String(content || '');
  const faqHeaderPattern = /^## (?:FAQ|What are (?:the )?(?:most )?common (?:questions|faqs).*\?)/im;
  const headerMatch = body.match(faqHeaderPattern);
  if (!headerMatch) return [];

  const start = body.indexOf(headerMatch[0]) + headerMatch[0].length;
  const remainder = body.slice(start);
  const nextH2Index = remainder.search(/\n## [^#]/);
  const faqBody = nextH2Index >= 0 ? remainder.slice(0, nextH2Index) : remainder;

  const entries = [];
  const headingRegex = /### (.+?)\n\n([\s\S]*?)(?=\n### |\n## |$)/g;
  let match;
  while ((match = headingRegex.exec(faqBody)) !== null) {
    entries.push({
      question: compactText(match[1]).replace(/\?*$/, '?'),
      answer: sanitizeBlogText(match[2]),
    });
  }

  if (entries.length > 0) return entries;

  const legacyRegex = /\*\*Q:\s*(.+?)\*\*\s*\n\s*A:\s*([\s\S]*?)(?=\n\s*\*\*Q:|$)/g;
  while ((match = legacyRegex.exec(faqBody)) !== null) {
    entries.push({
      question: compactText(match[1]).replace(/\?*$/, '?'),
      answer: sanitizeBlogText(match[2]),
    });
  }

  return entries;
}

function buildGeneratedFaqs(post) {
  const topic = buildTopicLabel(post);
  const goodFit = compactText(post.goodFitIf);
  const notGoodFit = compactText(post.notGoodFitIf);
  const tools = humanJoin(post.tools || []);

  if (post.template === 'head-to-head') {
    return [
      {
        question: `Should I choose ${tools}?`,
        answer: `Choose based on the workflow constraint that matters most first: deployment model, integration depth, budget, and how much operational complexity your team can absorb. This comparison is meant to narrow the decision, not declare a winner. Pilot the likeliest fit against a representative workload before committing.`,
      },
      {
        question: `Can teams use ${tools} together?`,
        answer: `Often, yes. Teams regularly combine tools when one handles a narrow layer better than another, such as retrieval, orchestration, editing, or production serving. The practical check is not whether the tools can coexist, but whether the added architecture complexity is justified by clearer performance, governance, or workflow benefits.`,
      },
      {
        question: 'What usually changes the decision most?',
        answer: `The decision usually changes when one tool aligns better with the team's existing stack, deployment rules, and ownership model. Pricing alone rarely decides the outcome. Integration overhead, observability, security review, and the amount of custom logic required often matter more once the workflow moves beyond a prototype.`,
      },
      {
        question: 'How should teams validate fit before adoption?',
        answer: `Run a bounded pilot using representative inputs, success criteria, and failure cases. Track output quality, latency, total cost, integration effort, and operational risk. A short pilot often reveals whether the workflow problem is really capability-driven, or whether it is a systems issue around data quality, permissions, or monitoring.`,
      },
    ];
  }

  return [
    {
      question: `What is ${topic} used for?`,
      answer: `${topic} is usually evaluated for a specific workflow rather than as a general winner. Teams look at whether it reduces manual work, fits their stack, and behaves predictably under real operating constraints. The strongest decision signal comes from representative tasks, not vendor positioning language.`,
    },
    {
      question: `When is ${topic} a good fit?`,
      answer: goodFit
        ? `${goodFit.replace(/\.$/, '')}. Teams should still validate setup effort, observability, and operational ownership before rollout. A good initial fit does not remove the need to test with representative data, user expectations, and failure cases.`
        : `${topic} is a stronger fit when it matches the workflow, team skills, and infrastructure boundaries you already have. Validate whether it reduces manual effort without adding hidden operational burden around cost, monitoring, or governance before standardizing on it.`,
    },
    {
      question: `What limits should teams validate first about ${topic}?`,
      answer: notGoodFit
        ? `${notGoodFit.replace(/\.$/, '')}. Teams should also validate rate limits, integration gaps, and the amount of custom support work required once usage scales. Limits that seem manageable in a prototype can become a production blocker when multiple systems depend on the workflow.`
        : `Validate cost profile, integration depth, deployment constraints, and how much manual oversight the workflow still needs. Many adoption issues come from operational limits rather than core capability limits, so testing should include failure handling, monitoring, and permissions from the start.`,
    },
    {
      question: `How should teams evaluate ${topic} in practice?`,
      answer: `Use a narrow pilot with representative inputs, expected outputs, and explicit success criteria. Compare workflow impact, implementation effort, and failure recovery, not just feature lists. The most reliable evaluation combines official documentation, hands-on testing, and a review of how the tool fits into the surrounding stack and process.`,
    },
  ];
}

function renderFaqSection(post, existingFaqs) {
  const topic = buildTopicLabel(post);
  const items = [...existingFaqs];
  for (const candidate of buildGeneratedFaqs(post)) {
    if (items.length >= 5) break;
    if (!items.some((item) => compactText(item.question).toLowerCase() === compactText(candidate.question).toLowerCase())) {
      items.push(candidate);
    }
  }

  const normalizedItems = items.slice(0, 5).map((item) => {
    const answer = compactText(stripMarkdown(item.answer || ''));
    const leadSentence = answer.split(/(?<=[.!?])\s+/)[0] || answer;
    const rest = answer.slice(leadSentence.length).trim();
    return {
      question: compactText(item.question).replace(/\?*$/, '?'),
      answer: `**${toSentence(leadSentence)}**${rest ? ` ${rest}` : ''}`.trim(),
    };
  });

  const lines = [
    `## What are common questions about ${topic}?`,
    '',
    `**These are the recurring questions teams ask when evaluating ${topic} for a real workflow.**`,
    '',
  ];

  normalizedItems.forEach((item) => {
    lines.push(`### ${item.question}`);
    lines.push('');
    lines.push(item.answer);
    lines.push('');
  });

  return lines.join('\n').trim();
}

function buildEvidenceSection(post, hasExternalLink) {
  const topic = buildTopicLabel(post);
  const lines = [
    '## What is the evidence basis?',
    '',
    '**This analysis draws on public documentation, pricing pages, Touchstone research notes, and practitioner reports where available.**',
    `- **Topic reviewed:** ${topic}`,
    `- **Category:** ${compactText(post.category) || 'AI tools'}`,
    `- **Last reviewed:** ${formatHumanDate(post.lastVerified) || post.lastVerified || 'Recently reviewed'}`,
    hasExternalLink
      ? '- **Sources used:** Existing citations within this article plus Touchstone research notes.'
      : '- **Sources used:** Existing Touchstone documentation and article-level review notes at [Touchstone](https://touchstone.guide/blog).',
  ];

  return lines.join('\n');
}

function buildRelatedLinksSection(post, allPosts, minLinks) {
  const currentSlug = compactText(post.slug);
  const related = (allPosts || [])
    .filter((candidate) => candidate && candidate.slug !== currentSlug)
    .map((candidate) => {
      const sharedTools = (candidate.tools || []).filter((tool) => (post.tools || []).includes(tool)).length;
      const sameCategory = compactText(candidate.category) === compactText(post.category) ? 3 : 0;
      return {
        slug: candidate.slug,
        title: candidate.title,
        score: sameCategory + sharedTools,
      };
    })
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

  const links = [];
  for (const item of related) {
    if (links.length >= Math.max(minLinks, 6)) break;
    links.push(`- [${compactText(item.title)}](/blog/${item.slug})`);
  }

  while (links.length < minLinks) {
    if (!links.includes('- [Touchstone blog index](/blog)')) {
      links.push('- [Touchstone blog index](/blog)');
    } else if (!links.includes('- [Touchstone database](/database)')) {
      links.push('- [Touchstone database](/database)');
    } else if (!links.includes('- [Touchstone homepage](/home)')) {
      links.push('- [Touchstone homepage](/home)');
    } else {
      break;
    }
  }

  return [
    '## What related Touchstone articles should you read next?',
    '',
    '**The links below provide adjacent Touchstone coverage so you can compare fit, surrounding infrastructure, and implementation tradeoffs.**',
    '',
    ...links,
  ].join('\n');
}

function stripFooter(content) {
  return String(content || '').replace(/\n^---\s*$[\s\S]*$/m, '').trim();
}

function splitLongParagraphs(content) {
  return String(content || '')
    .split(/\n\n/)
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return trimmed;
      if (/^(#{1,6}\s|\||- |\* |\d+\. |```|> )/.test(trimmed)) return trimmed;

      const sentences = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
      if (sentences.length <= 4) return trimmed;

      const chunks = [];
      for (let index = 0; index < sentences.length; index += 3) {
        chunks.push(sentences.slice(index, index + 3).join(' '));
      }
      return chunks.join('\n\n');
    })
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function applyPostSpecificFixes(post, content) {
  let next = String(content || '');

  if (post.slug === 'ai-tools-for-vector-search') {
    next = next.replace(
      /## What should you know about Tool-by-Tool Analysis\?\n\n([\s\S]*?)\n\n## What should you know about Good Fit Scenarios\?/,
      `## What should you know about Tool-by-Tool Analysis?

**The tool-by-tool breakdown clarifies where each vector database is strongest, what tradeoffs matter most, and which operating constraints usually shape adoption.**

### What should you know about Pinecone?

**Pinecone is a managed vector database optimized for speed, scale, and low-latency retrieval.** It is often chosen when teams want production-grade performance without managing infrastructure directly.

**Strengths:** Optimized for speed and scale, excellent filtering, managed service.
**Limitations:** Limited infrastructure control, potentially higher cost at very high scale.

### What should you know about Weaviate?

**Weaviate combines vector search with graph-style data relationships and flexible schema design.** It is often a fit when teams need hybrid retrieval patterns and more control over deployment.

**Strengths:** Graph capabilities, flexible schema, both managed and self-managed options.
**Limitations:** Can be complex to configure, especially for users unfamiliar with graph databases.

### What should you know about Qdrant?

**Qdrant is an open-source vector database focused on efficient retrieval, payload filtering, and practical deployment.** It is often evaluated by teams that want strong filtering and easier operations than larger distributed systems.

**Strengths:** Ease of deployment and use, good balance of features, strong filtering.
**Limitations:** Scalability potentially less extensive than Pinecone or Milvus.

### What should you know about Chroma?

**Chroma is a Python-native vector database built for experimentation, developer ergonomics, and smaller-scale projects.** It is commonly used in prototypes and early RAG systems where simplicity matters more than extreme scale.

**Strengths:** Easy to use, Python-native, good for prototyping.
**Limitations:** Limited scalability, fewer advanced features compared to other options.

### What should you know about PGVector?

**PGVector adds vector similarity search to PostgreSQL, making it attractive for teams that already run Postgres in production.** It is usually a fit when vector search needs to live close to relational data and the team wants to avoid another dedicated service.

**Strengths:** Integrates with PostgreSQL, leverages existing infrastructure.
**Limitations:** Performance potentially lower than dedicated vector databases, limited scalability.

### What should you know about Milvus?

**Milvus is an open-source vector database built for very large datasets and distributed deployments.** It is often evaluated when scale, indexing flexibility, and infrastructure control matter more than simplicity.

**Strengths:** High scalability, supports complex indexing techniques, open-source.
**Limitations:** Complex deployment and management, steeper learning curve.

## What should you know about Good Fit Scenarios?`
    );
  }

  return next;
}

function ensureLastVerifiedFooter(content, lastVerified) {
  const clean = stripFooter(content);
  const dateLabel = formatHumanDate(lastVerified) || compactText(lastVerified);
  return `${clean}\n\n---\n\n*Last reviewed: ${dateLabel}*`;
}

function fitDescriptionLength(value) {
  let text = compactText(value);
  if (text.length < 120) {
    text = `${text} Fit depends on context, workflow, and implementation constraints.`;
  }
  if (text.length <= 160) return text;

  const slice = text.slice(0, 157);
  const safeCut = slice.lastIndexOf(' ');
  return `${(safeCut > 110 ? slice.slice(0, safeCut) : slice).trim().replace(/[,:;]+$/, '')}.`;
}

function buildDescription(post, normalizedTitle) {
  const topic = buildTopicLabel({ ...post, title: normalizedTitle });
  const tools = humanJoin(post.tools || []);
  if (post.template === 'head-to-head') {
    return `Neutral comparison of ${tools}. Covers capabilities, pricing, limitations, and workflow fit for teams making context-dependent decisions.`;
  }
  if (post.template === 'tool-profile') {
    return `Neutral overview of ${topic}. Covers capabilities, pricing, limitations, integrations, and workflow fit for real evaluation work.`;
  }
  if (post.template === 'task-guide' || post.template === 'workflow-pattern') {
    return `Neutral guide to ${topic}. Covers workflow steps, tool tradeoffs, setup constraints, and fit signals for real teams.`;
  }
  return `Neutral overview of ${topic}. Covers major options, tradeoffs, pricing signals, and workflow fit without rankings or promotional claims.`;
}

function normalizeDescription(post, normalizedTitle) {
  const existing = compactText(post.description);
  if (existing.length >= 120 && existing.length <= 160) {
    return fitDescriptionLength(sanitizeBlogText(existing));
  }
  return fitDescriptionLength(buildDescription(post, normalizedTitle));
}

function buildShortTitle(post) {
  const tools = post.tools || [];
  const topic = buildTopicLabel(post);

  if (post.template === 'head-to-head' && tools.length >= 2) {
    return `${tools.join(' vs ')}: Fit Guide`;
  }
  if (post.template === 'tool-profile') {
    return `What is ${tools[0] || topic}? Fit Guide`;
  }
  if (post.template === 'task-guide' || post.template === 'workflow-pattern') {
    return compactText(post.title).replace(/:.*$/, '');
  }
  if (post.template === 'category-overview') {
    return compactText(post.title)
      .replace(/: The Complete Landscape/gi, ': Landscape')
      .replace(/: Landscape Overview/gi, ': Landscape')
      .replace(/, [^:]+$/, '');
  }

  return compactText(post.title);
}

function normalizeTitle(post) {
  let title = compactText(post.title)
    .replace(/\bSelection Guide\b/gi, 'Fit Guide')
    .replace(/\bLandscape Overview\b/gi, 'Landscape')
    .replace(/\bThe Complete Landscape\b/gi, 'Landscape');

  if (title.length + 15 <= 65) return title;

  const shorter = buildShortTitle(post);
  if (shorter.length + 15 <= 65) return shorter;

  if (title.includes(':')) {
    const beforeColon = title.split(':')[0];
    if (beforeColon.length + 15 <= 65) return beforeColon;
  }

  return title.length > 50 ? `${title.slice(0, 47).trim()}...` : title;
}

function normalizeDirectAnswer(post, description) {
  let text = sanitizeBlogText(compactText(post.directAnswer));

  if (!text) {
    const topic = buildTopicLabel(post);
    if (post.template === 'head-to-head') {
      text = `${humanJoin(post.tools || [])} serve different priorities. This comparison focuses on capabilities, pricing, deployment tradeoffs, integration fit, and operating constraints rather than rankings. Pilot the likeliest fit against your workflow, budget, and stack before standardizing on it.`;
    } else {
      text = `${topic} is evaluated here as a context-dependent option, not a winner. This page explains what it does, where it fits, which limits matter most, and what teams should validate before adoption. Pilot it against representative work before making it part of a production workflow.`;
    }
  }

  if (wordCount(text) < 45) {
    text = `${text} Pilot it against representative work, budget constraints, and surrounding stack requirements before committing.`;
  }

  let words = compactText(text).split(/\s+/).filter(Boolean);
  if (words.length > 65) {
    words = words.slice(0, 65);
    text = `${words.join(' ').replace(/[,:;]+$/, '')}.`;
  }

  if (compactText(text) === compactText(description)) {
    text = `${text.replace(/\.$/, '')} Use a small pilot to validate fit before standardizing on it.`;
  }

  return compactText(text);
}

function wrapTitleLines(title) {
  const words = compactText(title).split(' ');
  const lines = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > 24 && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines.slice(0, 3);
}

function buildHeroImage(post) {
  const [startColor, endColor] = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.default;
  const lines = wrapTitleLines(post.title);
  const category = compactText(post.category) || 'Touchstone research';
  const titleLines = lines
    .map((line, index) => `<text x="80" y="${250 + (index * 68)}" fill="#f8fafc" font-size="52" font-family="Inter, Arial, sans-serif" font-weight="700">${line.replace(/&/g, '&amp;')}</text>`)
    .join('');

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="630" viewBox="0 0 1200 630">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${startColor}" />
          <stop offset="100%" stop-color="${endColor}" />
        </linearGradient>
      </defs>
      <rect width="1200" height="630" fill="url(#bg)" />
      <circle cx="1010" cy="135" r="140" fill="rgba(255,255,255,0.08)" />
      <circle cx="1080" cy="520" r="220" fill="rgba(20,184,166,0.12)" />
      <text x="80" y="92" fill="#ccfbf1" font-size="28" font-family="Inter, Arial, sans-serif" letter-spacing="4">TOUCHSTONE</text>
      <text x="80" y="150" fill="#d4d4d8" font-size="24" font-family="Inter, Arial, sans-serif">${category.replace(/&/g, '&amp;')}</text>
      ${titleLines}
      <text x="80" y="560" fill="#d4d4d8" font-size="24" font-family="Inter, Arial, sans-serif">Neutral research, comparisons, and workflow fit analysis</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function deriveHeroImageAlt(post) {
  if (!post.heroImage) return '';
  if (compactText(post.heroImageAlt)) return compactText(post.heroImageAlt);
  return `${compactText(post.title)} hero image for Touchstone research`;
}

function deriveReadTime(post) {
  if (compactText(post.readTime)) return compactText(post.readTime);
  const words = wordCount(post.content);
  const minutes = Math.max(3, Math.round(words / 180));
  return `${minutes} min`;
}

function buildProofPoints(post) {
  const topic = buildTopicLabel(post);
  return [
    `Reviewed as ${compactText(post.template || 'analysis').replace(/-/g, ' ')} content for ${topic}.`,
    `Last reviewed on ${formatHumanDate(post.lastVerified) || compactText(post.lastVerified)}.`,
    `${wordCount(post.content)} words of supporting article content after normalization.`,
    `Categorized under ${compactText(post.category) || 'AI tools'} with ${Math.max(1, (post.tools || []).length)} named tool reference${(post.tools || []).length === 1 ? '' : 's'}.`,
  ];
}

function normalizeBlogContent(post, allPosts = []) {
  const raw = sanitizeBlogText(String(post.content || '').replace(/^# .+\n+/, '').trim());
  const { intro, sections } = parseSections(raw);
  const normalizedSections = [];
  const existingFaqs = extractExistingFaqs(raw);

  const hasOpeningQuestion = sections[0]?.level === 2 && sections[0]?.heading.endsWith('?');
  if (intro || !hasOpeningQuestion) {
    normalizedSections.push(buildQuickAnswerSection(post, intro));
  }

  sections.forEach((section) => {
    const question = toQuestionHeading(section.heading, section.level, post);
    const body = ensureBoldLead(sanitizeBlogText(section.body), question, post);
    normalizedSections.push({
      level: section.level,
      heading: question,
      body,
    });
  });

  let content = normalizedSections
    .map((section) => `${'#'.repeat(section.level)} ${section.heading}\n\n${section.body}`.trim())
    .join('\n\n')
    .trim();

  if (!/Touchstone is a neutral research platform/i.test(content)) {
    const disclosure = [
      '## What is Touchstone and how do we stay neutral?',
      '',
      '**Touchstone is a neutral research platform that documents how AI tools are used in practice without rankings, paid placements, or affiliate incentives.**',
      '- No affiliate links',
      '- No sponsored language',
      '- No promotional calls to action',
    ].join('\n');
    content = `${normalizedSections[0] ? `${'#'.repeat(normalizedSections[0].level)} ${normalizedSections[0].heading}\n\n${normalizedSections[0].body}\n\n` : ''}${disclosure}\n\n${normalizedSections.slice(1).map((section) => `${'#'.repeat(section.level)} ${section.heading}\n\n${section.body}`.trim()).join('\n\n')}`.trim();
  }

  if (!/## What is the evidence basis\?/i.test(content)) {
    content = `${content}\n\n${buildEvidenceSection(post, /\]\(https?:\/\//.test(content))}`;
  }

  const faqCount = extractExistingFaqs(content).length;
  if (faqCount < 4) {
    content = content
      .replace(/\n## (?:FAQ|What are (?:the )?(?:most )?common (?:questions|faqs).*\?)[\s\S]*$/im, '')
      .trim();
    content = `${content}\n\n${renderFaqSection(post, existingFaqs)}`;
  }

  const bodyWordCount = wordCount(content);
  const minLinks = bodyWordCount < 1500 ? 4 : bodyWordCount < 2500 ? 6 : 9;
  const currentLinks = (content.match(/\]\(\//g) || []).length;
  if (currentLinks < minLinks) {
    content = `${content}\n\n${buildRelatedLinksSection(post, allPosts, minLinks)}`;
  }

  if (!/\]\(https?:\/\//.test(content)) {
    content = `${content}\n\n## What source should you verify next?\n\n**Verify the latest capabilities, limits, and pricing against current documentation before rollout.** Review Touchstone research notes at [Touchstone](https://touchstone.guide/blog) alongside the product materials you already use for procurement and architecture review.`;
  }

  let finalized = stripFooter(content);
  finalized = splitLongParagraphs(finalized);

  if (extractExistingFaqs(finalized).length < 4 && !/## What are common questions about/i.test(finalized)) {
    finalized = `${finalized}\n\n${renderFaqSection(post, existingFaqs)}`.trim();
  }

  const finalMinLinks = wordCount(finalized) < 1500 ? 4 : wordCount(finalized) < 2500 ? 6 : 9;
  if ((finalized.match(/\]\(\//g) || []).length < finalMinLinks && !/## What related Touchstone articles should you read next\?/i.test(finalized)) {
    finalized = `${finalized}\n\n${buildRelatedLinksSection(post, allPosts, finalMinLinks)}`.trim();
  }

  if (wordCount(finalized) >= 1000 && !/\]\(https?:\/\//.test(finalized) && !/## What source should you verify next\?/i.test(finalized)) {
    finalized = `${finalized}\n\n## What source should you verify next?\n\n**Verify the latest capabilities, limits, and pricing against current documentation before rollout.** Review Touchstone research notes at [Touchstone](https://touchstone.guide/blog) alongside the product materials you already use for procurement and architecture review.`.trim();
  }

  finalized = applyPostSpecificFixes(post, finalized);

  return ensureLastVerifiedFooter(finalized, post.lastVerified);
}

export function normalizeToolRecord(tool, fallbackDate = '') {
  const normalized = {
    ...tool,
    slug: compactText(tool.slug),
  };

  normalized.description = buildNeutralToolDescription(tool);
  normalized.goodFitIf = compactText(tool.goodFitIf) || buildGoodFit(tool);
  normalized.notGoodFitIf = compactText(tool.notGoodFitIf) || buildNotGoodFit(tool);
  normalized.lastReviewed =
    normalizeDate(tool.lastReviewed) ||
    normalizeDate(tool.last_verified) ||
    normalizeDate(tool.enriched_at) ||
    normalizeDate(fallbackDate);

  return normalized;
}

export function normalizeBlogPost(post, _index = 0, allPosts = []) {
  const normalized = { ...post };
  const category = compactText(post.category) || 'AI tools';
  const categoryLabel = category === 'AI Tools' ? 'AI tool' : category.toLowerCase();

  normalized.author = compactText(post.author) || DEFAULT_AUTHOR_KEY;
  normalized.title = normalizeTitle(post);
  normalized.datePublished = normalizeDate(post.datePublished) || normalizeDate(post.publishedAt);
  normalized.dateModified =
    normalizeDate(post.dateModified) ||
    normalizeDate(post.updatedAt) ||
    normalizeDate(post.lastVerified) ||
    normalized.datePublished;
  normalized.lastVerified =
    normalizeDate(post.lastVerified) ||
    normalized.dateModified ||
    normalized.datePublished;
  normalized.goodFitIf =
    compactText(post.goodFitIf) ||
    `You are evaluating ${categoryLabel} options for a real workflow or architecture decision.`;
  normalized.notGoodFitIf =
    compactText(post.notGoodFitIf) ||
    'You need a universal winner instead of a context-based fit analysis.';
  normalized.description = normalizeDescription(post, normalized.title);
  normalized.directAnswer = normalizeDirectAnswer({ ...post, title: normalized.title }, normalized.description);
  normalized.content = normalizeBlogContent(normalized, allPosts);
  normalized.heroImage = compactText(post.heroImage) || buildHeroImage(normalized);
  normalized.heroImageAlt = deriveHeroImageAlt(normalized);
  normalized.readTime = deriveReadTime(normalized);
  normalized.proofPoints = Array.isArray(post.proofPoints) && post.proofPoints.length > 0
    ? post.proofPoints.map((item) => compactText(item)).filter(Boolean)
    : buildProofPoints(normalized);

  return normalized;
}
